// Очередь действий курьера в IndexedDB (без зависимостей). Порядок строго FIFO,
// повтор с тем же id (Idempotency-Key) не создаёт дубль. Если IndexedDB недоступен
// (приватный режим, старый WebView) — очередь в памяти.

export interface QueuedAction {
  /** Ключ идемпотентности; повторный enqueue с тем же id игнорируется. */
  id: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  body?: unknown
  headers?: Record<string, string>
  /** ISO-время создания на устройстве (client_at). */
  createdAt: string
  /** Подпись для UI: «Забрал SH-329». */
  label?: string
}

export interface StoredAction extends QueuedAction {
  /** Сколько раз отправка вернула retry/ошибку. */
  attempts: number
}

export type SendResult = 'ok' | 'retry' | 'drop'
export type Sender = (action: StoredAction) => Promise<SendResult>

export interface FlushResult {
  sent: number
  dropped: number
  remaining: number
}

interface Backend {
  add(a: StoredAction): Promise<boolean>
  first(): Promise<{ seq: number; action: StoredAction } | null>
  remove(seq: number): Promise<void>
  update(seq: number, a: StoredAction): Promise<void>
  all(): Promise<StoredAction[]>
  count(): Promise<number>
  clear(): Promise<void>
}

const STORE = 'actions'

function stripSeq(row: StoredAction & { seq?: number }): StoredAction {
  const copy: StoredAction & { seq?: number } = { ...row }
  delete copy.seq
  return copy
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

function idbBackend(dbName: string): Backend {
  let dbp: Promise<IDBDatabase> | null = null
  const open = () => {
    if (!dbp) {
      dbp = new Promise<IDBDatabase>((resolve, reject) => {
        const r = indexedDB.open(dbName, 1)
        r.onupgradeneeded = () => {
          const s = r.result.createObjectStore(STORE, { keyPath: 'seq', autoIncrement: true })
          s.createIndex('id', 'id', { unique: true })
        }
        r.onsuccess = () => resolve(r.result)
        r.onerror = () => reject(r.error)
        r.onblocked = () => reject(new Error('indexedDB blocked'))
      })
      dbp.catch(() => {
        dbp = null
      })
    }
    return dbp
  }
  const tx = async (mode: IDBTransactionMode) => (await open()).transaction(STORE, mode).objectStore(STORE)
  const done = (s: IDBObjectStore) =>
    new Promise<void>((resolve, reject) => {
      s.transaction.oncomplete = () => resolve()
      s.transaction.onerror = () => reject(s.transaction.error)
      s.transaction.onabort = () => reject(s.transaction.error)
    })

  return {
    async add(a) {
      const s = await tx('readwrite')
      const finished = done(s)
      const existing = await req(s.index('id').getKey(a.id))
      if (existing !== undefined) {
        await finished
        return false
      }
      s.add({ ...a })
      await finished
      return true
    },
    async first() {
      const s = await tx('readonly')
      const cur = await req(s.openCursor())
      if (!cur) return null
      const row = cur.value as StoredAction & { seq: number }
      return { seq: row.seq, action: stripSeq(row) }
    },
    async remove(seq) {
      const s = await tx('readwrite')
      const finished = done(s)
      s.delete(seq)
      await finished
    },
    async update(seq, a) {
      const s = await tx('readwrite')
      const finished = done(s)
      s.put({ ...a, seq })
      await finished
    },
    async all() {
      const s = await tx('readonly')
      const rows = (await req(s.getAll())) as (StoredAction & { seq: number })[]
      return rows.map(stripSeq)
    },
    async count() {
      const s = await tx('readonly')
      return req(s.count())
    },
    async clear() {
      const s = await tx('readwrite')
      const finished = done(s)
      s.clear()
      await finished
    },
  }
}

function memoryBackend(): Backend {
  let seq = 0
  const rows: { seq: number; action: StoredAction }[] = []
  return {
    async add(a) {
      if (rows.some((r) => r.action.id === a.id)) return false
      rows.push({ seq: ++seq, action: { ...a } })
      return true
    },
    async first() {
      const r = rows[0]
      return r ? { seq: r.seq, action: { ...r.action } } : null
    },
    async remove(s) {
      const i = rows.findIndex((r) => r.seq === s)
      if (i >= 0) rows.splice(i, 1)
    },
    async update(s, a) {
      const r = rows.find((x) => x.seq === s)
      if (r) r.action = { ...a }
    },
    async all() {
      return rows.map((r) => ({ ...r.action }))
    },
    async count() {
      return rows.length
    },
    async clear() {
      rows.length = 0
    },
  }
}

export interface OfflineQueue {
  /** Добавить действие. false — действие с таким id уже в очереди. */
  enqueue(action: Omit<QueuedAction, 'createdAt'> & { createdAt?: string }): Promise<boolean>
  size(): Promise<number>
  /** Последний известный размер (синхронно, для UI). */
  readonly currentSize: number
  list(): Promise<StoredAction[]>
  clear(): Promise<void>
  /** Подписка на изменение размера. Возвращает отписку. */
  subscribe(cb: (size: number) => void): () => void
  /**
   * Последовательная отправка по порядку. ok/drop — удалить и дальше; retry или исключение —
   * остановиться (порядок сохраняется). Параллельный вызов вернёт тот же промис.
   */
  flush(sender: Sender): Promise<FlushResult>
}

export function createQueue(opts: { dbName?: string; memory?: boolean } = {}): OfflineQueue {
  const useIdb = !opts.memory && typeof indexedDB !== 'undefined'
  let backend: Backend = useIdb ? idbBackend(opts.dbName ?? 'dalat-offline') : memoryBackend()
  const listeners = new Set<(n: number) => void>()
  let currentSize = 0
  let flushing: Promise<FlushResult> | null = null

  // Если IndexedDB падает при открытии — переключаемся на память, чтобы действия не терялись в рантайме.
  const run = async <T>(fn: (b: Backend) => Promise<T>): Promise<T> => {
    try {
      return await fn(backend)
    } catch (e) {
      if (!useIdb) throw e
      console.warn('[offline] IndexedDB unavailable, using memory queue', e)
      backend = memoryBackend()
      return fn(backend)
    }
  }

  const notify = async () => {
    currentSize = await run((b) => b.count())
    for (const cb of listeners) cb(currentSize)
  }

  // Узнать размер очереди, оставшейся с прошлого запуска.
  if (typeof window !== 'undefined') notify().catch(() => {})

  return {
    async enqueue(a) {
      const action: StoredAction = {
        id: a.id,
        method: a.method,
        path: a.path,
        body: a.body,
        headers: a.headers,
        label: a.label,
        createdAt: a.createdAt ?? new Date().toISOString(),
        attempts: 0,
      }
      const added = await run((b) => b.add(action))
      if (added) await notify()
      return added
    },
    size: () => run((b) => b.count()),
    get currentSize() {
      return currentSize
    },
    list: () => run((b) => b.all()),
    async clear() {
      await run((b) => b.clear())
      await notify()
    },
    subscribe(cb) {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
    flush(sender) {
      if (flushing) return flushing
      flushing = (async () => {
        let sent = 0
        let dropped = 0
        try {
          for (;;) {
            const head = await run((b) => b.first())
            if (!head) break
            let res: SendResult
            try {
              res = await sender(head.action)
            } catch {
              res = 'retry'
            }
            if (res === 'retry') {
              await run((b) => b.update(head.seq, { ...head.action, attempts: head.action.attempts + 1 }))
              break
            }
            await run((b) => b.remove(head.seq))
            if (res === 'ok') sent++
            else dropped++
            await notify()
          }
          return { sent, dropped, remaining: await run((b) => b.count()) }
        } finally {
          flushing = null
        }
      })()
      return flushing
    },
  }
}
